// BottomNav — spodní navigace na mobilu (screens/m01-header.md §„Spodní navigace",
// mockup design/BottomNav.dc.html). Importuje ji každá mobilní obrazovka.
//
// Shoptet nic takového nemá → celý markup je náš (5 statických položek).
// Se zapnutým chatem SupportBoxu (supportChat.js) ustoupí Domů položce Chat
// (Tomáš 2026-10-03: plovoucí kolečko na mobilu ne, chat patří do lišty; domů
// vede logo). Chat stojí vedle Kategorií, Objednávky vedle Košíku (2026-10-09). Chat je `button` se vstupem `data-bs-chat`, klik chytá
// delegovaný handler supportChat.js.
// „Kategorie" neotvírá URL, ale tentýž drawer jako hamburger (mobileNav.js),
// proto je to `button`, ne odkaz. Zbytek jsou obyčejné odkazy.
// „Objednávky" vedou na Stav objednávky (Tomáš 2026-10-09, místo Hledat:
// řádek hledání drží sticky hlavička trvale).
//
// Lišta se NESTAVÍ tam, kde by kolidovala:
//   - detail produktu (`body.type-product`) → místo ní sticky ATC lišta,
//   - pokladna (`body.ordering-process`) → slim chrome bez navigace.
// Viditelnost pod $bp-md řídí CSS; `html.bs-bottomnav-ready` zároveň odsazuje
// spodek stránky o výšku lišty (--bs-h-bottomnav).
//
// Idempotentní. Počet v košíku jede přes ShoptetCartUpdated (shoptet-hooks.js).

import { icon } from './icon.js';
import { onCartUpdated, cartState } from '../shoptet-hooks.js';
import { openMobileNav, closeMobileNav } from './mobileNav.js';
import { accountLink, accountWholesale } from './headerTools.js';
import { accountLabel } from './wholesalePrice.js';
import { VO } from '../locale/cs/common.js';
import { bodyClass, page, withPages } from './pages.js';
import { asChatTrigger, chatOn, registerLauncher } from './supportChat.js';
import L from '../locale/cs/bottomNav.js';

const LABEL = L.nav;

// „Účet" míří podle přihlášení na /login/ nebo /klient/ (#223) — href se
// dosazuje až v initBottomNav, ne při importu modulu.
const ITEMS = withPages([
  { key: 'home', label: L.home, icon: 'home', href: '/' },
  { key: 'cats', label: L.cats, icon: 'menu', action: 'nav' },
  { key: 'account', label: L.account, icon: 'user' },
  { key: 'orders', label: L.orders, icon: 'package', href: page('orderStatus') },
  { key: 'cart', label: L.cart, icon: 'cart', href: page('cart'), badge: true },
]);
const CHAT_ITEM = { key: 'chat', label: L.chat, icon: 'chat', action: 'chat', dot: true };

/** Položky lišty: s chatem bez Domů a Chat hned za Kategoriemi, jinak beze změny. */
export function navItems(chat) {
  if (!chat) return ITEMS;
  const items = ITEMS.filter((i) => i.key !== 'home');
  items.splice(items.findIndex((i) => i.key === 'cats') + 1, 0, CHAT_ITEM);
  return items;
}

// Aktivní položka podle sekce. Shoptet dává typ stránky do tříd body
// (`type-index`, `type-category`, …) — spolehlivější než parsování URL.
function activeKey() {
  const b = document.body.classList;
  if (b.contains('ordering-process') || b.contains(bodyClass('cart'))) return 'cart';
  if (
    b.contains('customer-page') ||
    b.contains(bodyClass('login')) ||
    b.contains(bodyClass('register')) ||
    [page('account'), page('login'), page('register')].some((p) => window.location.pathname.indexOf(p) === 0)
  ) {
    return 'account';
  }
  if (b.contains(bodyClass('orderStatus'))) return 'orders';
  if (b.contains('type-category') || b.contains('type-manufacturer-detail')) return 'cats';
  if (b.contains('type-index')) return 'home';
  return null;
}

function skip() {
  const b = document.body.classList;
  return b.contains('type-product') || b.contains('ordering-process');
}

function buildItem(item, active) {
  const tag = item.action ? 'button' : 'a';
  const node = document.createElement(tag);
  node.className = 'bs-bnav__item bs-bnav__item--' + item.key;
  if (item.action) {
    node.type = 'button';
  } else {
    node.href = item.href;
    if (item.key === active) node.setAttribute('aria-current', 'page');
  }
  if (item.key === active) node.classList.add('is-active');

  // Čárka nad ikonou = aktivní stav; v DOM je vždy, vykresluje ji CSS.
  const mark = document.createElement('span');
  mark.className = 'bs-bnav__mark';
  node.appendChild(mark);

  const iconWrap = document.createElement('span');
  iconWrap.className = 'bs-bnav__icon';
  iconWrap.appendChild(icon(item.icon));
  if (item.badge) {
    const badge = document.createElement('span');
    badge.className = 'bs-bnav__badge';
    iconWrap.appendChild(badge);
  }
  // nepřečtená odpověď operátora (supportChat.js syncUnread)
  if (item.dot) {
    const dot = document.createElement('span');
    dot.className = 'bs-bnav__dot';
    dot.setAttribute('aria-hidden', 'true');
    iconWrap.appendChild(dot);
  }
  // aktivní velkoobchodní ceník: štítek „VO" vpravo dole u ikony (headerTools.js)
  if (item.wholesale) {
    const tag = document.createElement('span');
    tag.className = 'bs-bnav__vo';
    tag.setAttribute('aria-hidden', 'true');
    tag.textContent = VO.tag;
    iconWrap.appendChild(tag);
    node.setAttribute('aria-label', accountLabel(item.wholesale, item.label));
  }
  node.appendChild(iconWrap);

  const label = document.createElement('span');
  label.className = 'bs-bnav__label';
  label.textContent = item.label;
  node.appendChild(label);

  if (item.action === 'nav') {
    node.addEventListener('click', function () {
      if (document.body.classList.contains('bs-drawer-open')) closeMobileNav();
      else openMobileNav(node);
    });
  } else if (item.action === 'chat') {
    asChatTrigger(node, 'bottomnav');
    registerLauncher(node);
  }

  return node;
}

function readCount(cart) {
  if (!cart) return 0;
  if (cart.itemsCount != null) return Number(cart.itemsCount) || 0;
  if (cart.count != null) return Number(cart.count) || 0;
  return 0;
}

export function initBottomNav() {
  if (document.querySelector('.bs-bnav') || skip()) return;

  const nav = document.createElement('nav');
  nav.className = 'bs-bnav';
  nav.setAttribute('aria-label', LABEL);

  const active = activeKey();
  for (const item of navItems(chatOn())) {
    const it = item.key === 'account' ? { ...item, href: accountLink().href, wholesale: accountWholesale() } : item;
    nav.appendChild(buildItem(it, active));
  }
  document.body.appendChild(nav);

  const badge = nav.querySelector('.bs-bnav__badge');
  const cartItem = nav.querySelector('.bs-bnav__item--cart');
  function render(cart) {
    const count = readCount(cart);
    cartItem.classList.toggle('has-items', count > 0);
    badge.textContent = count > 99 ? '99+' : String(count);
  }
  onCartUpdated(render);
  render(cartState());

  // Lišta stojí → teprve teď smí CSS odsadit spodek stránky.
  document.documentElement.classList.add('bs-bottomnav-ready');
}
